import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
type BaseButtonProps = {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  withIcon?: boolean;
  fullWidth?: boolean;
  children?: React.ReactNode;
  className?: string;
};
type ButtonAsButton = BaseButtonProps & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseButtonProps> & {
  href?: never;
};
type ButtonAsLink = BaseButtonProps & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseButtonProps> & {
  href: string;
};
type ButtonProps = ButtonAsButton | ButtonAsLink;
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  withIcon = false,
  fullWidth = false,
  href,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center rounded-full font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  const variants = {
    primary: 'bg-[#14B8A6] hover:bg-[#0D9488] text-white shadow-lg hover:shadow-xl focus:ring-[#14B8A6]',
    secondary: 'bg-[#F0FDFA] hover:bg-white text-[#4A3B32] border border-[#5EEAD4] hover:border-[#14B8A6] shadow-sm',
    outline: 'bg-transparent border-2 border-[#14B8A6] text-[#14B8A6] hover:bg-[#14B8A6] hover:text-white',
    ghost: 'bg-transparent hover:bg-[#F0FDFA] text-[#4A3B32]'
  };
  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg font-semibold'
  };
  const widthClass = fullWidth ? 'w-full' : '';
  const classNames = `${baseStyles} ${variants[variant]} ${sizes[size]} ${widthClass} ${className}`;
  const motionProps = {
    whileHover: {
      scale: 1.02
    },
    whileTap: {
      scale: 0.98
    }
  };
  if (href) {
    return <motion.a href={href} className={classNames} {...motionProps} {...props as React.AnchorHTMLAttributes<HTMLAnchorElement>} data-id="element-225">
        {children}
        {withIcon && <ArrowRight className="ml-2 h-5 w-5" data-id="element-226" />}
      </motion.a>;
  }
  return <motion.button className={classNames} {...motionProps} {...props as React.ButtonHTMLAttributes<HTMLButtonElement>} data-id="element-227">
      {children}
      {withIcon && <ArrowRight className="ml-2 h-5 w-5" data-id="element-228" />}
    </motion.button>;
}